import { DayPicker } from 'react-day-picker'
import 'react-day-picker/style.css'
import './DatePickerField.css'
import { CSSProperties, useMemo } from 'react'
import moment from 'moment'
import {
  DateAvailabilityOptions,
  firstAvailableDate,
  isDateAvailable,
  OperationHours,
} from '../../utils/bookingDate'
import { getReadablePrimaryButtonStyle } from '../../utils/buttonColor'

type DatePickerFieldProps = {
  value?: string
  onSelect: (date: Date) => void
  maxValidFrom: Date
  minValidTo: Date
  operationHours?: OperationHours
  themeColor: string
}

export function DatePickerField({
  value,
  onSelect,
  maxValidFrom,
  minValidTo,
  operationHours,
  themeColor,
}: DatePickerFieldProps) {
  const availability: DateAvailabilityOptions = useMemo(
    () => ({ maxValidFrom, minValidTo, operationHours }),
    [maxValidFrom, minValidTo, operationHours]
  )

  const selected = useMemo(
    () => (value ? moment(value, 'YYYY-MM-DD').toDate() : undefined),
    [value]
  )

  const defaultMonth = useMemo(
    () => selected ?? firstAvailableDate(availability) ?? maxValidFrom,
    [selected, availability, maxValidFrom]
  )

  // Resolve the accent the same way solid buttons do: `project.themeColor` may
  // be empty or invalid, in which case getReadablePrimaryButtonStyle falls back
  // to the default brand color. Feeding the raw themeColor straight into a CSS
  // variable would otherwise produce an invalid (white/transparent) day.
  const primaryStyle = getReadablePrimaryButtonStyle(themeColor)
  const accentColor = primaryStyle.backgroundColor as string
  const selectedTextColor = primaryStyle.color as string
  const wrapperStyle = {
    '--bx-accent': accentColor,
    '--bx-accent-text': selectedTextColor,
  } as CSSProperties
  const themeStyle = {
    '--rdp-accent-color': accentColor,
    '--rdp-accent-background-color': `${accentColor}14`,
    '--rdp-today-color': accentColor,
  } as CSSProperties

  return (
    <div className="rdp-booking flex justify-center" style={wrapperStyle}>
      <DayPicker
        style={themeStyle}
        mode="single"
        selected={selected}
        onSelect={(date) => {
          if (date) onSelect(date)
        }}
        disabled={(date) => !isDateAvailable(date, availability)}
        startMonth={maxValidFrom}
        endMonth={minValidTo}
        defaultMonth={defaultMonth}
        showOutsideDays
        weekStartsOn={1}
        components={{
          DayButton: ({ day: _day, modifiers, style, ...buttonProps }) => {
            let themedStyle: CSSProperties | undefined
            if (modifiers.selected) {
              themedStyle = {
                backgroundColor: accentColor,
                color: selectedTextColor,
                fontWeight: 700,
                border: 'none',
                boxShadow: '0 4px 10px -2px rgba(17, 24, 39, 0.35)',
              }
            } else if (modifiers.today && !modifiers.outside) {
              themedStyle = {
                color: accentColor,
                fontWeight: 700,
                boxShadow: `inset 0 0 0 1.5px ${accentColor}`,
              }
            }
            return <button {...buttonProps} style={{ ...style, ...themedStyle }} />
          },
        }}
      />
    </div>
  )
}
